/**
 * Fraud & Security Archive — read-only operational archive.
 * Same Fraud & Security Case ID (FRD-######) and Decision ID (DEC-FS-######).
 * No separate Archive ID.
 * Final Outcome: No Breach Found | Insufficient Evidence | Final Warning | Permanent Ban
 */

import {
  FINAL_DECISION_OPTIONS,
  QUEUE_CASE_TYPES,
  QUEUE_OWNER,
  display,
  formatAssignedDisplay,
  formatFraudDateTime,
  getTypeFilterOptions,
} from './fraudQueueSpec';

export const FRAUD_ARCHIVE_ROUTE = '/admin/fraud/archive';
export const ARCHIVE_DECISION_OWNER = 'Fraud & Security';

const RECEIVED = '2026-09-13T09:00:00+10:00';
const ARCHIVE_SAMPLE_DECIDED_BY = 'Sarah Nguyen';

const cloneCase = (row) => JSON.parse(JSON.stringify(row));

export const formatFraudArchiveDateTime = (value) => formatFraudDateTime(value);

/** Spec: retain the same Case ID — no FSA- Archive ID. */
export const archiveIdFromCaseId = (caseId) => {
  const raw = String(caseId || '').trim();
  if (!raw || raw === '—') return raw;
  const digits = raw.replace(/\D/g, '');
  if (!digits) return raw;
  return `FRD-${digits.padStart(6, '0')}`;
};

export const toArchivedCase = (queueCase, { handoffOn } = {}) => {
  const caseId = archiveIdFromCaseId(queueCase.caseId);
  const when = handoffOn || queueCase.finalisedOn || queueCase.handoffOn || RECEIVED;
  const decisionId =
    queueCase.decision?.decisionId ||
    queueCase.decisionId ||
    `DEC-FS-${String(caseId).replace(/\D/g, '').padStart(6, '0')}`;
  const alreadyHasHandoff = (queueCase.auditLog || []).some(
    (entry) => entry.event === 'Archive handoff' || entry.event === 'Moved to Archive'
  );
  const supportTeamMember = formatAssignedDisplay(
    queueCase.supportTeamMember ||
      queueCase.assignedTo ||
      queueCase.decision?.decidedBy ||
      ARCHIVE_SAMPLE_DECIDED_BY
  );

  return {
    ...cloneCase(queueCase),
    caseId,
    queueCaseId: caseId,
    archiveId: caseId,
    status: 'Decided',
    decisionOwner: ARCHIVE_DECISION_OWNER,
    owner: queueCase.owner || QUEUE_OWNER,
    handoffOn: when,
    finalisedOn: queueCase.decision?.finalisedOn || queueCase.finalisedOn || when,
    caseReason: queueCase.caseReason || '—',
    sourceFlagId: queueCase.sourceFlagId || '—',
    transferredFromCaseId: queueCase.transferredFromCaseId || '—',
    relatedSupportTicketId: queueCase.relatedSupportTicketId || '—',
    relatedAppealId: queueCase.relatedAppealId || null,
    supportTeamMember,
    assignedTo: supportTeamMember,
    decision: {
      ...(queueCase.decision || {}),
      decisionId,
      finalDecision: queueCase.decision?.finalDecision || queueCase.finalDecision || '—',
      decidedBy: queueCase.decision?.decidedBy || ARCHIVE_SAMPLE_DECIDED_BY,
      decidedOn: queueCase.decision?.decidedOn || when,
      finalisedOn: queueCase.decision?.finalisedOn || when,
      confirmed: true,
    },
    decisionId,
    auditLog: alreadyHasHandoff
      ? cloneCase(queueCase.auditLog || [])
      : [
          ...(queueCase.auditLog || []),
          {
            dateTime: when,
            event: 'Moved to Archive',
            details: `System; Lifecycle Control; ${caseId}`,
            record: caseId,
          },
        ],
  };
};

const archivedSample = ({
  caseId,
  source,
  caseType,
  caseReason,
  accountName,
  accountId,
  accountType,
  accountAvatar,
  priority,
  finalDecision,
  finalAccountAction,
  decisionId,
  sourceFlagId,
  transferredFromCaseId,
  relatedSupportTicketId,
  relatedAppealId,
  supportTeamMember,
  finalisedOn,
  receivedOn,
}) =>
  toArchivedCase(
    {
      caseId,
      source,
      caseType,
      caseReason: caseReason || '—',
      sourceFlagId: sourceFlagId || '—',
      transferredFromCaseId: transferredFromCaseId || '—',
      relatedSupportTicketId: relatedSupportTicketId || '—',
      relatedAppealId: relatedAppealId || null,
      accountName,
      accountId,
      accountType,
      accountAvatar,
      priority,
      supportTeamMember: supportTeamMember || ARCHIVE_SAMPLE_DECIDED_BY,
      assignedTo: supportTeamMember || ARCHIVE_SAMPLE_DECIDED_BY,
      owner: QUEUE_OWNER,
      receivedOn: receivedOn || RECEIVED,
      lastUpdated: finalisedOn || RECEIVED,
      finalisedOn: finalisedOn || RECEIVED,
      decisionId: decisionId || `DEC-FS-${String(caseId).replace(/\D/g, '').padStart(6, '0')}`,
      decision: {
        confirmedSeverity: priority,
        finalDecision,
        finalAccountAction: finalAccountAction || '—',
        evidenceSummary: `${caseType} reviewed for this ${source} case.`,
        decisionReason: 'Recorded findings support this outcome.',
        decisionId: decisionId || `DEC-FS-${String(caseId).replace(/\D/g, '').padStart(6, '0')}`,
        decidedBy: supportTeamMember || ARCHIVE_SAMPLE_DECIDED_BY,
        decidedOn: finalisedOn || RECEIVED,
        finalisedOn: finalisedOn || RECEIVED,
        confirmed: true,
      },
      linkedRecords: [],
      evidence: [],
      informationRequests: [],
      internalNotes: [],
      finalisation: [
        {
          step: 'Outcome notice',
          result: 'Available to the affected account',
          record: decisionId || `DEC-FS-${String(caseId).replace(/\D/g, '').padStart(6, '0')}`,
          lastAttempt: finalisedOn || RECEIVED,
          completed: finalisedOn || RECEIVED,
          status: 'Complete',
        },
      ],
      auditLog: [
        {
          dateTime: receivedOn || RECEIVED,
          event: 'Case receipt',
          details: `System; ${source}; ${caseType}`,
          record: caseId,
        },
        {
          dateTime: finalisedOn || RECEIVED,
          event: 'Decision confirmation',
          details: `${supportTeamMember || ARCHIVE_SAMPLE_DECIDED_BY}; ${finalDecision}; ${
            decisionId || ''
          }`,
          record: decisionId || caseId,
        },
      ],
    },
    { handoffOn: finalisedOn || RECEIVED }
  );

let archiveState = [
  archivedSample({
    caseId: 'FRD-000104',
    source: 'System',
    caseType: 'Account',
    caseReason: 'Suspected account takeover — matching security alert',
    sourceFlagId: 'FLG-90104',
    accountName: 'Olivia M.',
    accountId: 'CUS-008821',
    accountType: 'Customer',
    accountAvatar: 'https://i.pravatar.cc/150?img=47',
    priority: 'Medium',
    finalDecision: 'No Breach Found',
    finalAccountAction: '—',
    decisionId: 'DEC-FS-000104',
    supportTeamMember: 'Sarah Nguyen',
    finalisedOn: '2026-09-13T11:00:00+10:00',
  }),
  archivedSample({
    caseId: 'FRD-000201',
    source: 'Moderation',
    caseType: 'Post',
    caseReason: 'Suspected stolen images with fraud indicators',
    sourceFlagId: 'FLG-20491',
    transferredFromCaseId: 'MOD-000841',
    accountName: 'Werribee Plumbing Pty Ltd',
    accountId: 'BUS-9934',
    accountType: 'Business',
    accountAvatar: 'https://i.pravatar.cc/150?img=12',
    priority: 'High',
    finalDecision: 'Final Warning',
    finalAccountAction: 'Final Warning',
    decisionId: 'DEC-FS-000201',
    supportTeamMember: 'Alex Chen',
    finalisedOn: '2026-09-12T16:20:00+10:00',
  }),
  archivedSample({
    caseId: 'FRD-000202',
    source: 'Compliance',
    caseType: 'Licence',
    caseReason: 'Suspected forged plumbing licence documents',
    transferredFromCaseId: 'CMP-000218',
    relatedSupportTicketId: 'TKT-000440',
    accountName: 'RapidFix Plumbing Pty Ltd',
    accountId: 'BUS-4412',
    accountType: 'Business',
    accountAvatar: 'https://i.pravatar.cc/150?img=15',
    priority: 'Medium',
    finalDecision: 'Insufficient Evidence',
    finalAccountAction: '—',
    decisionId: 'DEC-FS-000202',
    supportTeamMember: 'Sarah Nguyen',
    finalisedOn: '2026-09-11T14:05:00+10:00',
  }),
  archivedSample({
    caseId: 'FRD-000203',
    source: 'Support Team',
    caseType: 'Account',
    caseReason: 'Confirmed takeover with Permanent Ban after recovery failed',
    sourceFlagId: 'FLG-90103',
    relatedSupportTicketId: 'TKT-000512',
    accountName: 'Daniel Morris',
    accountId: 'CUS-3349',
    accountType: 'Customer',
    accountAvatar: 'https://i.pravatar.cc/150?img=32',
    priority: 'High',
    finalDecision: 'Permanent Ban',
    finalAccountAction: 'Permanent Ban',
    decisionId: 'DEC-FS-000203',
    supportTeamMember: 'John Smith',
    relatedAppealId: null,
    finalisedOn: '2026-09-10T09:40:00+10:00',
  }),
  archivedSample({
    caseId: 'FRD-000204',
    source: 'Customer',
    caseType: 'Message',
    caseReason: 'Scam payment request in message thread',
    sourceFlagId: 'FLG-50105',
    accountName: 'Bright Spark Electrical',
    accountId: 'BUS-5502',
    accountType: 'Business',
    accountAvatar: 'https://i.pravatar.cc/150?img=28',
    priority: 'Low',
    finalDecision: 'No Breach Found',
    decisionId: 'DEC-FS-000204',
    supportTeamMember: 'Emma Wilson',
    finalisedOn: '2026-09-09T18:15:00+10:00',
  }),
  archivedSample({
    caseId: 'FRD-000205',
    source: 'Business',
    caseType: 'Quote',
    caseReason: 'Customer attempting off-platform payment fraud',
    sourceFlagId: 'FLG-55106',
    accountName: 'Jordan P.',
    accountId: 'CUS-9201',
    accountType: 'Customer',
    accountAvatar: 'https://i.pravatar.cc/150?img=45',
    priority: 'Medium',
    finalDecision: 'Final Warning',
    finalAccountAction: 'Final Warning',
    decisionId: 'DEC-FS-000205',
    supportTeamMember: 'Mike Davis',
    relatedAppealId: 'APL-000410',
    finalisedOn: '2026-09-08T12:00:00+10:00',
  }),
  archivedSample({
    caseId: 'FRD-000206',
    source: 'System',
    caseType: 'Business Profile Item',
    caseReason: 'Security match on profile contact details pattern',
    sourceFlagId: 'FLG-58007',
    accountName: 'QuickFlow Plumbing Services',
    accountId: 'BUS-6609',
    accountType: 'Business',
    accountAvatar: 'https://i.pravatar.cc/150?img=60',
    priority: 'Medium',
    finalDecision: 'Insufficient Evidence',
    decisionId: 'DEC-FS-000206',
    supportTeamMember: 'Sarah Nguyen',
    finalisedOn: '2026-09-07T10:30:00+10:00',
  }),
];

export const collectArchiveCases = () =>
  archiveState
    .map(cloneCase)
    .sort(
      (a, b) =>
        new Date(b.finalisedOn || b.decision?.finalisedOn || 0).getTime() -
        new Date(a.finalisedOn || a.decision?.finalisedOn || 0).getTime()
    );

export const findArchiveCase = (id) => {
  if (!id) return null;
  const n = String(id).replace(/^(?:FSA|FRD)-?/i, '');
  const found = archiveState.find((row) => {
    const caseDigits = String(row.caseId || '').replace(/^(?:FSA|FRD)-?/i, '');
    const archiveDigits = String(row.archiveId || '').replace(/^(?:FSA|FRD)-?/i, '');
    return caseDigits === n || archiveDigits === n || String(row.caseId) === String(id);
  });
  return found ? cloneCase(found) : null;
};

export const getArchivedFraudCaseIdSet = () => {
  const ids = new Set();
  archiveState.forEach((row) => {
    const n = String(row.caseId || '').replace(/\D/g, '');
    if (n) ids.add(n);
  });
  return ids;
};

export const handoffQueueCaseToArchive = (queueCase) => {
  if (!queueCase?.caseId) return null;
  const digits = String(queueCase.caseId).replace(/\D/g, '');
  const existing = archiveState.find(
    (row) => String(row.caseId || '').replace(/\D/g, '') === digits
  );
  if (existing) return cloneCase(existing);
  const archived = toArchivedCase(queueCase, {
    handoffOn: new Date().toISOString(),
  });
  archiveState.push(archived);
  return cloneCase(archived);
};

export const matchesArchiveSearch = (row, query) => {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    row.caseId,
    row.queueCaseId,
    row.decisionId || row.decision?.decisionId,
    row.accountName,
    row.accountId,
    row.sourceFlagId,
    row.transferredFromCaseId,
    row.caseReason,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
};

export const ARCHIVE_FINAL_OUTCOME_OPTIONS = [
  { value: 'All', label: 'All' },
  ...FINAL_DECISION_OPTIONS.map((value) => ({ value, label: value })),
];

export const ARCHIVE_FINALISED_DATE_OPTIONS = [
  { value: 'All', label: 'All' },
  { value: 'Last 7 Days', label: 'Last 7 Days' },
  { value: 'Last 30 Days', label: 'Last 30 Days' },
  { value: 'Last 90 Days', label: 'Last 90 Days' },
];

export const matchesFinalisedDate = (row, filters = {}) => {
  if (!filters.dateRange || filters.dateRange === 'All') return true;
  const finalised = new Date(row.finalisedOn || row.decision?.finalisedOn || row.handoffOn);
  if (Number.isNaN(finalised.getTime())) return false;

  const now = new Date();
  now.setHours(23, 59, 59, 999);
  const days =
    filters.dateRange === 'Last 7 Days'
      ? 7
      : filters.dateRange === 'Last 30 Days'
        ? 30
        : filters.dateRange === 'Last 90 Days'
          ? 90
          : null;
  if (!days) return true;
  const cutoff = new Date(now);
  cutoff.setDate(cutoff.getDate() - days);
  cutoff.setHours(0, 0, 0, 0);
  return finalised >= cutoff && finalised <= now;
};

export const matchesArchiveFilters = (row, filters = {}) => {
  if (filters.type && filters.type !== 'All' && (row.caseType || row.type) !== filters.type) {
    return false;
  }
  const outcome = row.decision?.finalDecision || row.finalDecision || '';
  if (filters.finalOutcome && filters.finalOutcome !== 'All' && outcome !== filters.finalOutcome) {
    return false;
  }
  if (filters.dateRange && filters.dateRange !== 'All') {
    if (!matchesFinalisedDate(row, filters)) return false;
  }
  return true;
};

export const getArchiveTypeFilterOptions = () => [
  { value: 'All', label: 'All' },
  ...QUEUE_CASE_TYPES.map((value) => ({ value, label: value })),
];

/** @deprecated Archive Screen 1 no longer filters by Source */
export const getArchiveSourceFilterOptions = () => [
  { value: 'All', label: 'All' },
];

/** List row — Case ID is the same FRD identity retained from Queue. */
export const archiveListRow = (row) => ({
  caseId: archiveIdFromCaseId(row.caseId),
  queueCaseId: archiveIdFromCaseId(row.caseId || row.queueCaseId),
  archiveId: archiveIdFromCaseId(row.caseId),
  decisionId: row.decisionId || row.decision?.decisionId || '—',
  source: row.source,
  caseType: row.caseType || '—',
  caseReason: row.caseReason || '—',
  accountType: row.accountType || '—',
  accountName: row.accountName,
  accountId: row.accountId,
  accountAvatar: row.accountAvatar,
  priority: row.priority,
  finalOutcome: row.decision?.finalDecision || row.finalDecision || '—',
  decision: row.decision?.finalDecision || row.finalDecision || '—',
  decidedBy: row.decision?.decidedBy || '—',
  supportTeam: formatAssignedDisplay(
    row.supportTeamMember || row.assignedTo || row.decision?.decidedBy
  ),
  finalisedOn: row.finalisedOn || row.decision?.finalisedOn || row.handoffOn,
  decidedOn: row.decision?.decidedOn,
});

export { display, formatAssignedDisplay, formatFraudDateTime, getTypeFilterOptions };
